// Original exercises for examining investment decisions, not trading rules.
export const behaviorLessons = [
  {
    id: 'loss-aversion', moduleId: 'behavior',
    title: '同樣的金額，為什麼「虧掉」特別難受？', subtitle: '參考點、損失感受與等價選項', minutes: 7, tag: '情緒與選擇',
    objectives: ['區分損失趨避、一般風險偏好與實際資金限制', '把不同說法改寫成相同的期末金額再比較'],
    paragraphs: [
      '損失趨避描述一種傾向：相對某個參考點，同額損失的負面感受可能大於獲利的正面感受。參考點可能是投入金額、昨天的餘額或原本期待的結果；它會影響我們如何命名一件事。這不是每個人、每個情境都有固定倍數的心理定律，也不等同於單純不喜歡任何不確定性。',
      '檢查時可以把「賺到、保住、認賠」暫時換成期末可用金額、機率、期限與成本。再問：如果數字完全一樣，只改說法，我還會選同一個選項嗎？這種練習能暴露框架的影響，但單次選擇不能診斷一個人有哪種偏誤，期望金額相同也不代表兩個風險方案同樣適合。',
      '不願接受虧損，可能讓人把評估一再延後；然而繼續持有或結束持有，本身都不足以證明有偏誤。資產的新資訊、交易成本、資金用途與承受能力仍要一起檢查。過去成本可用來記帳，卻不能讓市場負責恢復原來的餘額；本課練習檢查理由，不設定任何必須出場的跌幅。',
    ],
    example: {
      title: '假設：兩種說法，其實是同一組選項',
      steps: ['以 30,000 元為比較起點。甲方案確定留下 27,000 元；乙方案有 50% 機會留下 30,000 元、50% 機會留下 24,000 元，忽略其他成本。', '把它改成損失說法：甲確定損失 3,000 元；乙有 50% 機會不損失、50% 機會損失 6,000 元。兩個版本沒有更改任何最終金額或機率。', '乙的期望金額為 30,000 × 50% + 24,000 × 50% = 27,000 元，與甲相同，但乙仍有更分散的結果。', '先各自記下偏好的選項與理由，再並排比較。如果選擇改變，檢查是措辭造成，還是自己偷偷加入了題目沒有的假設。'],
      conclusion: '先對齊實際結果，再檢查損失感受；同一期望值不是要求所有人做相同選擇。',
    },
    pitfalls: ['把損失趨避簡化成每個人都一定怕虧損兩倍，忽略情境與個人差異。', '只因為一筆持有正在虧損，就判定繼續持有必然錯誤，或認為未賣出就沒有經濟損失。'],
    glossary: [{ term: '參考點', definition: '用來判斷某結果算獲利還是損失的比較基準，未必等於客觀價值。' }, { term: '框架效應', definition: '結果與條件相同，描述方式不同卻可能改變選擇；與損失感受相關，但不是同一概念。' }],
    takeaway: '把情緒用語改寫成金額與條件，才能看見自己真正願意承擔的結果。',
    exercise: '將一個讓你不想面對的財務選擇分別寫成「保留多少」與「減少多少」；保留同一時間點、成本及機率，再檢查理由是否改變。',
    quiz: { question: '兩段描述的期末金額與機率完全相同，只是分別強調「保住」與「損失」。檢查決策時應先做什麼？', options: ['看到損失兩字就一定改選風險較高的方案', '期望值相同，所以任何人的偏好都必須相同', '對齊金額與機率，再檢查措辭、資金需求及風險偏好'], answer: 2, explanation: '相同結果可以用不同框架描述；先核對等價性，再檢查理由，不能把期望值或心理偏誤名稱當成個人選擇的唯一答案。' },
    sources: [
      { label: '市場先生｜處置效應與損失感受的主題閱讀', url: 'https://rich01.com/disposition-effect/', kind: 'reading' },
      { label: 'Kahneman、Tversky｜展望理論原始研究', url: 'https://web.mit.edu/curhan/www/docs/Articles/15341_Readings/Behavioral_Decision_Theory/Kahneman_Tversky_1979_Prospect_theory.pdf', kind: 'official' },
    ],
  },
  {
    id: 'anchoring-bias', moduleId: 'behavior',
    title: '離最高價很遠，就一定算便宜嗎？', subtitle: '讓估計離開最先看到的數字', minutes: 7, tag: '獨立估計',
    objectives: ['辨認高點、目標價與買入價如何變成心理錨點', '用當前資料重新建立比較口徑，而非只看折價幅度'],
    paragraphs: [
      '定錨偏誤是估計過度受起始數字影響，後續調整不足。起點可能是歷史高價、第一篇報告的目標價，也可能是與價值無關的提示。知道那個數字沒有保證，並不代表它已經失去心理影響；因此可以先記下自己的資料與推算，再接觸他人的結論。',
      '一個可做的檢查是把起始價格遮住，重寫目前知道的營運、財務與風險條件。若要比較不同時點，須確認每股數字、股本變動、幣別與期間可比；公司獲利改變後，較低股價未必代表用更低的倍數買到相同獲利。下面用本益比展示這件事，並不是用某個倍數判定應買或應賣。',
      '獨立估計仍然可能錯誤，不是再找另一個有名的價格取代舊錨點。可以列出估計範圍、資料日期與會改變判斷的條件，保留不知道的部分。歷史價格是發生過的交易紀錄，目標價是有假設的看法；兩者都不是未來必須到達的位置。',
    ],
    example: {
      title: '假設：價格降了，獲利也變了',
      steps: ['某假想公司過去股價 240 元，同期可比較的年度 EPS 為 8 元，本益比為 240 ÷ 8 = 30 倍。', '現在股價 156 元，可比較年度 EPS 為 4 元；假設幣別、股數與會計口徑沒有改變，本益比為 156 ÷ 4 = 39 倍。', '價格變動為（156 ÷ 240 − 1）= −35%，也就是下跌 35%，但獲利下降 50%。「比高點低很多」沒有直接證明估值較低。', '39 倍也不能單獨證明一定太貴；還要確認獲利是否一次性、成長與風險等假設，不能只把舊高點當答案。'],
      conclusion: '價格比較要配上變化的分母與條件，不能把折價幅度當成估值結論。',
    },
    pitfalls: ['認為曾經到過的高價就是合理價，跌得越多就一定越值得買。', '先讀目標價再倒推假設，只挑能讓答案接近該數字的資料。'],
    glossary: [{ term: '心理錨點', definition: '影響後續判斷的起始數字，即使它與目前問題的關聯有限。' }, { term: '調整不足', definition: '雖然取得新資訊，估計仍停留在起點附近，沒有充分反映條件改變。' }],
    takeaway: '先整理自己的依據，再看別人的價格；有歷史高點，不等於有價值保證。',
    exercise: '選一份公開估值看法，先遮住結論價格，只讀假設並寫下仍不確定的三件事；最後再打開價格，記錄它是否改變了你原本的推理。',
    quiz: { question: '你剛看到一份更高的目標價，但沒有取得新營運資料，最適合怎麼處理？', options: ['核對它的假設與資料日期，不因新數字較高就自動上修自己的估計', '先把自己的估計提高到同一數字，之後再找理由', '認為目標價已經替未來價格作保證'], answer: 0, explanation: '目標價是特定假設下的估計。先理解資料和推理，能避免只是把原本的錨點換成另一個更醒目的數字。' },
    sources: [
      { label: '市場先生｜錨定偏見的主題閱讀', url: 'https://rich01.com/behavioral-economics-in-finance-10/', kind: 'reading' },
      { label: 'Tversky、Kahneman｜不確定性判斷與定錨研究', url: 'https://web.stanford.edu/~jlmcc/Presentations/tversky_kahneman_1974.pdf', kind: 'official' },
    ],
  },
  {
    id: 'confirmation-bias', moduleId: 'behavior',
    title: '我在查資料，還是在替原本的想法辯護？', subtitle: '把看法拆成可以失敗的假設', minutes: 8, tag: '證據檢查',
    objectives: ['將籠統的看好理由改寫成可核對的預期', '對支持與反對證據採取相同的品質標準'],
    paragraphs: [
      '確認偏誤不只出現在搜尋階段，也可能出現在解讀與記憶：支持自己的數字容易被記住，不合預期的數字則被說成暫時或不重要。實驗研究觀察到，人們可能偏好取得與既有偏好一致的資訊；這是理解行為的線索，不代表所有反對意見都比較可靠。',
      '把看法寫成「因為什麼，所以預期哪個指標在何時出現什麼變化」，再事先寫出哪些結果會削弱它。接著用同樣標準檢查兩邊：來源是否直接、期間是否可比、資料是否完整、推論能否重做。不是機械地湊齊一篇看多和一篇看空，也不是把文章篇數當成投票。',
      '原假設與資料不符時，可以降低信心、修正原因或承認暫時無法判斷，並保留變更紀錄。不要事後把衡量目標換成剛好有成長的項目，再聲稱原判斷一直正確。反證也可能有資料品質問題，核對後才能決定它對假設有多大影響；這個流程本身不直接給出交易指令。',
    ],
    example: {
      title: '假設：營收成長，是否驗證了毛利也成長？',
      steps: ['事前假設寫成：「這家公司收入增加時，毛利額也會增加。」前期營收 120 萬元、毛利額 36 萬元，毛利率 30%。', '本期營收 144 萬元、毛利額仍是 36 萬元。營收增加 20%，但毛利額增加 0%，毛利率降至 36 ÷ 144 = 25%。', '若只圈出營收成長 20%，就漏掉原假設要檢驗的毛利額。應確認成本、產品組合與資料口徑，再記錄這項證據如何改變信心。', '即使找到合理解釋，也要區分「這次假設沒有符合」與「提出新的待驗證假設」，不覆寫原本預期。'],
      conclusion: '把檢驗目標事先寫清楚，才不會等結果出現後一直移動判斷標準。',
    },
    pitfalls: ['要求反對證據完美無缺，卻只憑一張支持自己的截圖就相信。', '原本預期獲利成長，事後只看到收入成長就宣布預測成功。'],
    glossary: [{ term: '可檢驗假設', definition: '說明預期、觀察指標與時間，讓之後的資料有機會支持或削弱它。' }, { term: '選擇性解讀', definition: '對同一組資料只強調有利部分，讓既有看法不容易受到挑戰。' }],
    takeaway: '先定義什麼結果會讓自己改變看法，再讀資料。',
    exercise: '把一句「這家公司前景很好」改成一項有期限與指標的假設，再寫出會削弱它的結果，以及支持與反對資料都必須滿足的查證標準。',
    quiz: { question: '原假設是「營收增加會帶動毛利額增加」，結果營收上升、毛利額持平。哪個紀錄最合理？', options: ['只記營收成長，宣布原假設已被完整驗證', '記下毛利額未增加，查核原因後調整信心，並保留原假設', '立刻認定公司必然有問題，不需核對資料'], answer: 1, explanation: '結果沒有符合原來對毛利額的預期，應誠實記錄並查核原因；不能改用另一個成長指標宣布成功，也不能由一次落差直接推論所有事情。' },
    sources: [
      { label: '市場先生｜確認偏誤的主題閱讀', url: 'https://rich01.com/behavioral-economics-in-finance-10/', kind: 'reading' },
      { label: 'Judgment and Decision Making｜偏好如何影響資訊搜尋與評價', url: 'https://www.cambridge.org/core/journals/judgment-and-decision-making/article/preferencedriven-biases-in-decision-makers-information-search-and-evaluation/81BA843AD1AD0A672A440FDE84CCF1F8', kind: 'official' },
    ],
  },
  {
    id: 'herd-fomo', moduleId: 'behavior',
    title: '群組都在賺，我是不是一定錯過了？', subtitle: '社群樣本與害怕落後的壓力', minutes: 7, tag: '社群與情緒',
    objectives: ['分辨社群熱度、選擇性展示與獨立證據', '把急迫感拆成可查證的期限與自己的需求'],
    paragraphs: [
      'FOMO 是害怕錯過的感受，投資時可能表現為看到別人的成果，就覺得自己必須立刻做一樣的事。從眾則讓我們以他人的行動取代自己的判斷。兩者可能同時出現，但熱門不等於一定錯誤，也不代表反向操作必然有效；要檢查的是理由有沒有被社會壓力取代。',
      '社群畫面不是隨機抽樣的完整帳本。願意貼出的人可能與沉默的人不同，同一篇消息也可能被很多帳號重複轉貼。沒有所有人的投入、虧損、費用與期間，不能從少數獲利截圖推算整個群體的成功率。別人的可用資金、目標與風險，也不能直接當成自己的條件。',
      '可以事先寫一張暫停卡：我究竟怕錯過什麼？期限是可驗證的制度安排，還是別人催促的話？產品、費用、資金用途哪些尚未確認？由自己預先約定資料不足時的處理方式，而不是被倒數訊息臨時改掉流程。催促與群體壓力也常被詐騙利用，但出現 FOMO 本身不能單獨證明某投資是詐騙。',
    ],
    example: {
      title: '假設：200 人群組裡的 12 張獲利截圖',
      steps: ['某群組有 200 名成員，一週內有 12 位成員貼出獲利截圖；不知道其他人是否投資，也不知道貼圖者是否還有未展示的虧損。', '12 ÷ 200 = 6%，只能表示這週貼出獲利截圖的人占群組人數的比例，不能說群組投資成功率就是 6%，也不能說所有投資者都賺錢。', '若其中多人引用同一則未查證消息，轉貼數也不是獨立查證次數。先補齊原始資料，再回到自己寫好的資金需求與查核條件。'],
      conclusion: '熱鬧畫面可以提醒你有話題需要理解，不能替缺少的分母與風險資料作答。',
    },
    pitfalls: ['把沒有參與一段漲幅當成帳戶已經產生同額虧損。', '看到很多人做相同行為就略過查核，或因為反感從眾而認定反向做一定賺。'],
    glossary: [{ term: '選擇性展示', definition: '可看到的內容是被選擇公布的部分，未必代表所有參與者及完整結果。' }, { term: 'FOMO', definition: 'Fear of Missing Out，害怕錯過機會或落後他人的感受，可能增加匆促行動的壓力。' }],
    takeaway: '先辨認自己看見的是什麼樣本，再處理害怕落後的感受。',
    exercise: '替自己寫一張三行暫停卡：我的急迫感來自哪裡、還缺哪項資料、哪些既定資金用途不能被臨時忽略。用一則社群貼文演練即可，不必實際下單。',
    quiz: { question: '看到多位群友貼出獲利截圖，哪個判斷最合理？', options: ['貼圖者都獲利，所以沒有貼圖的人也一定獲利', '這種投資很熱門，所以反向操作一定正確', '展示的樣本不完整，須核對來源與條件，不能直接推算成功率'], answer: 2, explanation: '社群展示缺乏完整分母與損益資料，也可能有重複來源。人氣不足以證明安全或危險，更不能保證跟隨或反向操作的結果。' },
    sources: [
      { label: '市場先生｜主動投資與 FOMO 的主題閱讀', url: 'https://rich01.com/should-i-actively-invest/', kind: 'reading' },
      { label: 'SEC｜急迫感、FOMO 與投資壓力的查核', url: 'https://www.investor.gov/protect-your-investments/fraud/protect-your-money', kind: 'official' },
    ],
  },
  {
    id: 'overconfidence-trading', moduleId: 'behavior',
    title: '交易越勤，真的代表判斷越準嗎？', subtitle: '把信心放到完整的費後紀錄裡', minutes: 8, tag: '能力與成本',
    objectives: ['將自信、單次獲利與持續可驗證的能力分開', '用完整交易紀錄計算費後成果並檢查比較條件'],
    paragraphs: [
      '過度自信是信心超過證據所能支持的程度，例如把幾次獲利歸功於自己的判斷，卻把失敗全部歸因於外在因素。另一種形式是覺得預測非常精確，給出的可能範圍太窄。這不代表有信心就是錯誤；重點是能否用完整、可重查的紀錄檢驗能力，而不是只靠記得的成功經驗。',
      '交易頻率本身不能證明能力好壞，必要的資金調整也會產生交易。Barber 與 Odean 的特定歷史券商樣本觀察到，高交易群體的費後表現受到明顯拖累，研究結果與過度自信解釋一致；它不是對每位現代投資人的保證結論，也不能把每次交易都診斷成心理問題。',
      '實作上可以同時記錄原本的預期、所有成交與未成交決定，以及費前和費後結果。比較時需對齊期間、幣別、現金流與承擔的風險；漲市中賺錢未必表示具有額外判斷能力。更完整的紀錄可能顯示能力不足，也可能顯示資料太少，不能靠增加交易次數來證明自己一定有優勢。',
    ],
    example: {
      title: '假設：先看獲利，再補上每一趟成本',
      steps: ['某段期間期初資金 80,000 元，沒有外部入出金，共完成 20 趟買入再賣出的交易；全部交易的費前損益合計賺 1,600 元。', '本例假設每趟買賣合計成本固定 60 元，已完整包含本例要計入的費用與成交摩擦，總成本為 20 × 60 = 1,200 元；這不是實際市場費率。', '費後損益為 1,600 − 1,200 = 400 元，期間費後報酬 400 ÷ 80,000 = 0.5%；費前報酬則為 2%。', '只展示獲利交易或費前 2%，會遺漏重要資料。即使費後為正，仍須與相同口徑的比較基準及承擔風險一起檢查，不能直接宣告能力已獲證實。'],
      conclusion: '信心需要費後、完整而可比較的紀錄支持；做得忙不等於做得有效。',
    },
    pitfalls: ['把成功都歸功於能力、失敗都歸咎於市場，讓自己的判斷永遠不會被修正。', '只記成交後賺錢的交易，漏掉虧損、成本與原本曾排除的替代選項。'],
    glossary: [{ term: '過度自信', definition: '對能力或預測精確度的信心高於現有證據所能支持的程度。' }, { term: '自利歸因', definition: '傾向把好結果歸因於自身能力，把壞結果歸因於外部因素。' }],
    takeaway: '用全部費後結果檢查判斷，而不是用交易次數或幾次成功替自己打分。',
    exercise: '用紙上模擬或既有紀錄，列出期間、期初資金、外部入出金、全部損益與成本；另列你當時的預期，再寫下資料還不足以回答哪些能力問題。',
    quiz: { question: '一份紀錄顯示全部交易費前賺 1,600 元，20 趟交易每趟完整成本 60 元；沒有其他成本。費後損益是多少？', options: ['賺 400 元，但不能僅憑這個數字確認持續能力', '賺 1,600 元，交易成本與能力無關所以不用扣', '虧 1,200 元，應忽略所有費前損益'], answer: 0, explanation: '總成本 20 × 60 = 1,200 元，費後剩 400 元。完整金額是評估的起點，能力判斷還需要期間、風險、比較基準與足夠資料。' },
    sources: [
      { label: '市場先生｜過度自信與頻繁交易的主題閱讀', url: 'https://rich01.com/invest-beginners-guide-12/', kind: 'reading' },
      { label: 'Barber、Odean｜個人投資者交易與費後績效研究', url: 'https://faculty.haas.berkeley.edu/odean/papers/returns/Individual_Investor_Performance_Final.pdf', kind: 'official' },
    ],
  },
  {
    id: 'decision-journal', moduleId: 'behavior',
    title: '留下當時的想法，才不會事後總覺得早知道', subtitle: '決策日誌與過程、結果分開回顧', minutes: 8, tag: '建立紀錄',
    objectives: ['建立可保留版本的事前決策紀錄', '區分結果好壞、流程品質與長期預測校準'],
    paragraphs: [
      '結果偏誤是知道最後的好壞後，連帶改變對當時決策品質的評價。事後之明則讓人覺得結果本來就很容易預見。實驗研究在特定情境觀察到結果會影響決策評分；不能因此推論每次獲利都是運氣，也不能把一次虧損當成原流程必然失敗。',
      '本課的日誌是原創整理格式：事前寫下日期、待決定事項、當時可取得的資料、主要假設、替代選項、未知條件與回顧日期。有需要時，可替明確可判定的事件寫下機率估計，並註明這是主觀預期。回顧時追加實際結果與新資訊，保留原文；不要在結果公布後把事前理由改成看似一直正確。',
      '回顧分成兩欄：一欄檢查資料是否按當時條件取得、計算是否正確、是否遵守預先寫的限制；另一欄記錄實際損益或事件是否發生。累積相似的機率預測後，才能逐步觀察信心是否校準；小樣本、事件相關與情境差異都會限制結論。寫日誌不保證獲利，它提供的是能誠實學習的紀錄。',
    ],
    example: {
      title: '假設：20 筆預測都留下原始版本',
      steps: ['小安先後記下 20 個定義清楚、已有判定期限的事件，對每個事件發生都估計 70%；這只是練習樣本，並非產品報酬保證。', '期限到後，20 筆都能判定，其中 12 筆發生，實際比例為 12 ÷ 20 = 60%，比原本 70% 低 10 個百分點。', '他保留全部 20 筆，逐項補上結果與當時漏看的資料，沒有刪掉未發生的 8 筆，也沒有把原機率改成 60%。', '20 筆不足以確認長期校準程度，還要考慮事件是否相似、是否受同一事件影響。一次沒發生，也不會單獨推翻「有 70% 機會」的說法。'],
      conclusion: '原始預期與後來結果都留下，才有機會檢查信心；事後改寫會失去學習依據。',
    },
    pitfalls: ['只保留成功紀錄，或在結果公布後刪改原理由，讓自己看起來從沒判斷錯。', '因為一次賺錢就判定流程完美，或因一次沒發生就認為機率預測毫無意義。'],
    glossary: [{ term: '結果偏誤', definition: '過度受到已知結果影響，回頭評價當初在有限資訊下所做的決策。' }, { term: '校準', definition: '長期比較某機率等級的預測與相應事件發生比例；不是要求每一筆都符合最可能的結果。' }],
    takeaway: '日誌保留的是當時可知的理由；回顧追加學到的事，不改寫歷史。',
    exercise: '為一項紙上決策建立日誌：日期、問題、資料、假設、替代選項、未知條件、回顧日。到期後另加「過程檢查」與「實際結果」兩欄，保留所有舊版本。',
    quiz: { question: '回顧發現結果與事前預期不同，決策日誌應如何更新？', options: ['把事前預期直接改成實際結果，以免留下錯誤', '保留原始紀錄，另加實際結果、資訊差異與流程檢查', '只刪掉失敗紀錄，留下值得學習的成功紀錄'], answer: 1, explanation: '保留事前版本才能知道當時真正相信什麼；追加回顧可以區分資料不足、流程問題與不確定結果，而不是讓結果倒過來改寫過去。' },
    sources: [
      { label: '市場先生｜從決策錯誤與紀錄中學習的主題閱讀', url: 'https://rich01.com/ray-dalio-bridgewater-associates/', kind: 'reading' },
      { label: 'Aiyer 等｜結果偏誤的預註冊重複實驗', url: 'https://pubmed.ncbi.nlm.nih.gov/40951810/', kind: 'official' },
    ],
  },
];
